<template>
  <el-tooltip :content="tooltip" placement="top">
    <span class="device-status-badge" :class="statusClass">
      <span class="status-dot"></span>
      {{ label }}
    </span>
  </el-tooltip>
</template>

<script>
import { ElTooltip } from 'element-plus';
import i18n from '@/i18n';

// Devices wake twice a day and heartbeat on every connection, but only send a
// new location when they have moved. lastContact is the latest heartbeat —
// green means the device is alive even if its last location fix is old.
const ACTIVE_MAX_HOURS = 48;
const WARNING_MAX_HOURS = 7 * 24;

// Elsewhere in the app `$i18n.locale` gets assigned a plain string, which
// replaces the composer's locale ref — so handle both shapes
export function appLocale() {
  const locale = i18n.global.locale;
  return typeof locale === 'string' ? locale : locale.value;
}

export function isInContact(lastContact) {
  if (!lastContact) return false;
  const ts = new Date(lastContact).getTime();
  if (Number.isNaN(ts)) return false;
  return (Date.now() - ts) / 3600000 < ACTIVE_MAX_HOURS;
}

export default {
  name: 'DeviceStatusBadge',
  components: { ElTooltip },
  props: {
    lastContact: {
      type: String,
      default: null,
    },
  },
  computed: {
    appLocale() {
      // In composition mode i18n locale is a ref — this.$i18n.locale would hand
      // Intl a ref object, which silently falls back to the browser locale
      return appLocale();
    },
    hoursSince() {
      if (!this.lastContact) return null;
      const ts = new Date(this.lastContact).getTime();
      if (Number.isNaN(ts)) return null;
      return (Date.now() - ts) / 3600000;
    },
    status() {
      if (this.hoursSince == null) return 'unknown';
      if (this.hoursSince < ACTIVE_MAX_HOURS) return 'active';
      if (this.hoursSince < WARNING_MAX_HOURS) return 'warning';
      return 'lost';
    },
    statusClass() {
      return `device-status-${this.status}`;
    },
    relativeTime() {
      // Via i18n messages, not Intl.RelativeTimeFormat — Chromium lacks
      // Icelandic (and other) locale data there and silently falls back to English
      if (this.hoursSince == null) return '';
      if (this.hoursSince < 1) return this.$t('tools.relMinutesAgo', { n: Math.max(1, Math.round(this.hoursSince * 60)) });
      if (this.hoursSince < 24) return this.$t('tools.relHoursAgo', { n: Math.round(this.hoursSince) });
      const days = Math.round(this.hoursSince / 24);
      if (days === 1) return this.$t('tools.relYesterday');
      return this.$t('tools.relDaysAgo', { n: days });
    },
    label() {
      if (this.status === 'unknown') return this.$t('tools.deviceNoData');
      const text = this.status === 'active'
        ? this.$t('tools.deviceInContact')
        : this.$t('tools.deviceNoContact');
      return `${text} · ${this.relativeTime}`;
    },
    tooltip() {
      const explanation = this.$t('tools.deviceOnlyReportsWhenMoved');
      if (!this.lastContact) return explanation;
      const time = new Date(this.lastContact).toLocaleString(this.appLocale);
      return `${this.$t('tools.deviceLastContactAt', { time })} ${explanation}`;
    },
  },
};
</script>

<style scoped>
.device-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: default;
  vertical-align: middle;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: currentColor;
  flex-shrink: 0;
}

.device-status-active {
  color: #1e7e34;
  background-color: rgba(40, 167, 69, 0.12);
}

.device-status-warning {
  color: #997404;
  background-color: rgba(255, 193, 7, 0.18);
}

.device-status-lost {
  color: #dc3545;
  background-color: rgba(220, 53, 69, 0.1);
}

.device-status-unknown {
  color: #6c757d;
  background-color: rgba(108, 117, 125, 0.12);
}

/* Dark mode: light status colours on a tint of themselves (the light-mode ones are ~2.4:1 on navy) */
body.dark-version .device-status-active {
  color: var(--ts-success);
  background-color: rgba(95, 208, 138, 0.16);
}

body.dark-version .device-status-warning {
  color: var(--ts-warning);
  background-color: rgba(246, 185, 74, 0.16);
}

body.dark-version .device-status-lost {
  color: var(--ts-danger);
  background-color: rgba(255, 138, 128, 0.16);
}

body.dark-version .device-status-unknown {
  color: var(--ts-text-muted);
  background-color: rgba(168, 175, 192, 0.16);
}
</style>
